import React, { useEffect, useState } from 'react';
import get from 'get-value';
import classNames from 'classnames';
import { Grid } from '@material-ui/core';
import { removeArrayBrackets } from 'utils';
import { isChecked } from 'utils/isChecked';

import { QuoteFields } from './QuoteField';
import FormControl from 'shared/FormControl/FormControl';

import './_QuoteRadio.scss';
import { HelperContent } from 'shared/HelperText/HelperTextInterface';

type Helper = {
  [key in string]: string;
};

interface IQuoteRadio extends QuoteFields {
  options: any[];
  helpers?: Helper;
  selected?: string;
  clickHandler?: (value: any) => void;
  helperContent?: HelperContent;
  showIcon?: boolean;
  radioWidth?: any;
}

export const QuoteRadio: React.FC<IQuoteRadio> = ({
  field,
  options,
  labelText,
  paragraphText,
  form: { errors, touched },
  helperText,
  helperContent,
  selected,
  clickHandler,
  required,
  disabled,
  helpers,
  showIcon = true,
  radioWidth = 6,
}) => {
  const [selectedVal, setSelectedVal] = useState(selected || field.value);
  const fieldName = removeArrayBrackets(field.name);
  const isValid = get(errors, fieldName) && get(touched, fieldName);
  const isCorrect = !get(errors, fieldName) && get(touched, fieldName);

  const handleClick = (value: any) => {
    setSelectedVal(value);
    if (typeof clickHandler === 'function') clickHandler(value);
  };

  useEffect(() => {
    setSelectedVal(selected || field.value);
  }, [selected, field.value]);

  return (
    <FormControl
      labelText={labelText}
      paragraphText={paragraphText}
      isCorrect={isCorrect}
      error={isValid}
      helperText={helperText}
      helperContent={helperContent}
      required={required}
      fieldName={fieldName}
      showErrorIcon={showIcon}
      showSuccessIcon={showIcon}>
      <div className="quote-radio" tabIndex={0}>
        <div className="radio-buttons">
          <Grid container>
            {options.map(({ option, value }, index) => {
              const checked = isChecked(value, selectedVal);

              return (
                <Grid key={`${option}-${index}`} item xs={radioWidth}>
                  <label className="radio-buttons__label" tabIndex={0}>
                    <input
                      className="radio-buttons__input"
                      {...field}
                      type="radio"
                      name={field.name}
                      value={value}
                      checked={checked}
                      onClick={() => !disabled && handleClick(value)}
                      disabled={disabled}
                    />
                    <div
                      className={classNames('checkbox-buttons__button button', {
                        disabled,
                        'value-checked': checked,
                      })}>
                      {option}
                    </div>
                  </label>
                </Grid>
              );
            })}
          </Grid>
        </div>
        {helpers && (
          <div className="inline-helper">
            {Object.keys(helpers).map(
              (helperKey: string) =>
                helperKey === selectedVal && <p key={`helper-${helperKey}`}>{helpers[helperKey]}</p>,
            )}
          </div>
        )}
      </div>
    </FormControl>
  );
};
